// NewsPage.tsx — Section II: ICP News.
//
// "Upgrade-pressure signals from the network. Six sources, one wire, refreshed daily at 06:00 UTC."
// (Lore-approved NW-03)
//
// Wire:
//   - useAdminNews() polls /admin/news (Cash B1.9 Railway endpoint).
//   - Source filter chips: All / DFX / Motoko / II / DFINITY / Forum / Skills.
//   - Loading / empty / error states per Lore-approved NW-64..NW-68.
//   - Refresh button + last-refreshed indicator (NW-04 / NW-05 pattern).
//
// All copy strings are EXACTLY the Lore-approved Final column.

import React from 'react';
import { useAdminNews, type NewsSource } from '../hooks/useAdminNews';
import NewsItemComponent from '../components/NewsItem';

const SOURCES: NewsSource[] = ['All', 'DFX', 'Motoko', 'II', 'DFINITY', 'Forum', 'Skills'];

// Spell out small integers in editorial prose per NW-06 pattern.
const SPELLED_OUT: Record<number, string> = {
  0: 'Zero', 1: 'One', 2: 'Two', 3: 'Three', 4: 'Four', 5: 'Five',
  6: 'Six', 7: 'Seven', 8: 'Eight', 9: 'Nine', 10: 'Ten',
  11: 'Eleven', 12: 'Twelve',
};
function spellOut(n: number): string {
  return n <= 12 ? (SPELLED_OUT[n] ?? String(n)) : String(n);
}

// Format ISO datetime to "YYYY-MM-DD · HH:MM UTC"
function formatRefreshTime(iso: string | null): string {
  if (!iso) return '—';
  // e.g. "2026-04-28T06:00:00Z" -> "2026-04-28 · 06:00 UTC"
  const [datePart, timePart] = iso.split('T');
  const time = (timePart ?? '').replace('Z', '').slice(0, 5);
  return `${datePart ?? iso} · ${time} UTC`;
}

export default function NewsPage(): React.ReactElement {
  const {
    filteredItems,
    activeFilter,
    setFilter,
    isLoading,
    error,
    lastRefresh,
    nextRefresh,
    totalLast7d,
    refresh,
  } = useAdminNews();

  return (
    <section className="section" id="news" aria-labelledby="news-title">
      <header className="section-header">
        <span className="section-eyebrow">Section II</span>
        <h2 className="section-title" id="news-title">ICP News</h2>
        <p className="section-lede">
          Upgrade-pressure signals from the network. Six sources, one wire, refreshed daily at 06:00 UTC.
        </p>
        {/* Meta row — NW-04 / NW-05 / NW-06 */}
        <div className="news-meta" aria-live="polite">
          <span>Last refresh · {formatRefreshTime(lastRefresh)}</span>
          <span className="news-meta-divider" aria-hidden="true">·</span>
          <span>Next refresh · {formatRefreshTime(nextRefresh)}</span>
          <span className="news-meta-divider" aria-hidden="true">·</span>
          <span>{spellOut(totalLast7d)} items in the last seven days</span>
        </div>
      </header>

      {/* Refresh control */}
      <div className="news-controls">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={refresh}
          disabled={isLoading}
        >
          {isLoading ? 'Refreshing' : 'Refresh'}
        </button>
      </div>

      {/* Source filter chips — NW-07..NW-13 */}
      <div className="news-filters" role="group" aria-label="Filter by source">
        {SOURCES.map((src) => (
          <button
            key={src}
            type="button"
            className={`news-chip${activeFilter === src ? ' is-active' : ''}`}
            aria-pressed={activeFilter === src}
            onClick={() => setFilter(src)}
          >
            {src}
          </button>
        ))}
      </div>

      {/* State machine: loading / error / empty / items */}
      {isLoading && filteredItems.length === 0 ? (
        // NW-63 / NW-64
        <div className="news-state" role="status" aria-live="polite">
          <p className="news-state-label">Loading</p>
          <p className="news-state-text">Refreshing the wire.</p>
        </div>
      ) : error ? (
        // NW-67 / NW-68
        <div className="news-state" role="alert">
          <p className="news-state-label">Error state</p>
          <p className="news-state-text">One source is temporarily silent · {error}</p>
        </div>
      ) : filteredItems.length === 0 ? (
        // NW-65 / NW-66
        <div className="news-state">
          <p className="news-state-label">Empty state</p>
          <p className="news-state-text">No news in the last thirty days.</p>
        </div>
      ) : (
        // Item list — sorted newest first (NW-AU-03 ordering rule)
        <ul className="news-list" aria-label="ICP news items, newest first">
          {filteredItems.map((item) => (
            <NewsItemComponent key={item.id} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}
